import React from 'react';
import { parseJSON } from 'date-fns';

import { Text } from '@reckon-web/text';
import { Flex } from '@reckon-web/flex';
import { ArrowRightIcon } from '@reckon-web/icon';

import { DateUtils } from '../../../utils/date-utils';
import { getRecurrenceFrequencyLabel } from '../../../globals';
import type { GetActivityItem } from '../../../data/useGetActivities';

interface IActivityCategoryRecurrenceUpdated {
  activity: GetActivityItem;
}

const getParsed = (raw: string | null | undefined) => {
  if (!raw) {
    return null;
  }
  return JSON.parse(raw) as IRecurrence;
};

// ActivityCategory RecurrenceUpdated = 6
export const ActivityCategoryRecurrenceUpdated = ({
  activity,
}: IActivityCategoryRecurrenceUpdated) => {
  const oldParsed = getParsed(activity.propertyOld);
  const newParsed = getParsed(activity.propertyNew);

  const getParsedFormattedCorrectedDateFormatted = (
    parsed: IRecurrence | null | undefined
  ) => {
    if (!parsed || !parsed.NextDateUtc) {
      return '';
    }
    const parsedDate = parseJSON(parsed.NextDateUtc);
    // TODO: will need to format time to tenant local date time.
    const correctedDate = parsedDate;
    return DateUtils.formatDateShort(correctedDate);
  };
  const oldDateFormatted = getParsedFormattedCorrectedDateFormatted(oldParsed);
  const newDateFormatted = getParsedFormattedCorrectedDateFormatted(newParsed);

  const getEnabled = (parsed: IRecurrence | null | undefined) => {
    if (!parsed) {
      return false;
    }
    if (parsed.Enabled === null || parsed.Enabled === undefined) {
      return false;
    }
    return parsed.Enabled;
  };
  const oldEnabled = getEnabled(oldParsed);
  const newEnabled = getEnabled(newParsed);

  const getFrequencyFormatted = (parsed: IRecurrence | null | undefined) => {
    if (!parsed) {
      return '';
    }
    if (
      parsed.RecurrenceFrequency === null ||
      parsed.RecurrenceFrequency === undefined
    ) {
      return '';
    }
    const frequency = getRecurrenceFrequencyLabel(parsed.RecurrenceFrequency);
    if (!frequency) {
      return 'Unknown';
    }
    return frequency;
  };

  const oldFrequencyFormatted = getFrequencyFormatted(oldParsed);
  const newFrequencyFormatted = getFrequencyFormatted(newParsed);

  return (
    <Flex>
      {oldEnabled && newEnabled ? (
        <Flex direction="column" gap="small">
          <Text size="small">Edited recurrence</Text>
          <Flex gap="xsmall" alignItems="center">
            <Text size="small" weight="semibold">
              Every {oldFrequencyFormatted}, next {oldDateFormatted}
            </Text>
            <Flex flexShrink={0}>
              <ArrowRightIcon size="small" color="dim" />
            </Flex>
            <Text size="small" weight="semibold">
              Every {newFrequencyFormatted}, next {newDateFormatted}
            </Text>
          </Flex>
        </Flex>
      ) : !oldEnabled && newEnabled ? (
        <Flex direction="column" gap="small">
          <Text size="small">Set a recurrence</Text>
          <Text size="small" weight="semibold">
            Every {newFrequencyFormatted}, next {newDateFormatted}
          </Text>
        </Flex>
      ) : oldEnabled && !newEnabled ? (
        <Flex direction="column" gap="small">
          <Text size="small">Disabled recurrence. Was:</Text>
          <Text size="small" weight="semibold">
            Every {oldFrequencyFormatted}, next {oldDateFormatted}
          </Text>
        </Flex>
      ) : (
        <Text>Updated recurrence</Text>
      )}
    </Flex>
  );
};

// a very cutdown version of what we care about from the serialized JSON. There's a bunch of other properties
// but this is all we care about for deserialisation
interface IRecurrence {
  NextDateUtc?: string | null; // "0001-01-01T00:00:00",
  RecurrenceFrequency?: number | null; // 3,
  Enabled?: boolean | null; // false,
}
